import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Mic, Square, Play, Pause, Trash2, Loader2, Languages, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../../context/LanguageContext';
import { myiaTexts } from '../../utils/myiaTranslations';
import { usePremiumEnabled } from '../../hooks/usePremiumEnabled';
import { ReportAIContentButton } from '../ReportAIContent';

/**
 * VoiceClonePanel — Let a user record a 30–60s voice sample, upload it to
 * ElevenLabs Instant Voice Cloning, then synthesize any text in 30+ languages
 * using their cloned voice.
 */
const LANGS = [
  ['fr', 'Français'], ['en', 'English'], ['es', 'Español'], ['de', 'Deutsch'],
  ['it', 'Italiano'], ['pt', 'Português'], ['nl', 'Nederlands'], ['ar', 'العربية'],
  ['ru', 'Русский'], ['zh', '中文'], ['ja', '日本語'], ['hi', 'हिन्दी'],
];

export default function VoiceClonePanel() {
  const { language } = useLanguage();
  const M = myiaTexts(language);
  const premiumOn = usePremiumEnabled();
  const [existing, setExisting] = useState(null);
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [text, setText] = useState('');
  const [lang, setLang] = useState('fr');
  const [synth, setSynth] = useState(null); // { url }
  const [synthesizing, setSynthesizing] = useState(false);
  const [playing, setPlaying] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    axios.get('/api/myia-voice/clone/mine').then(r => setExisting(r.data?.clone || null)).catch(() => {});
  }, []);

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(t => t.stop());
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds(s => s + 1), 1000);
    } catch (e) {
      toast.error(M.vcMicKo);
    }
  };

  const stopRec = () => {
    if (mediaRef.current && recording) {
      mediaRef.current.stop();
      setRecording(false);
      clearInterval(timerRef.current);
    }
  };

  const upload = async () => {
    if (!audioBlob) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('sample', audioBlob, 'sample.webm');
      fd.append('name', `Voix ${new Date().toLocaleDateString()}`);
      const r = await axios.post('/api/myia-voice/clone/create', fd);
      setExisting(r.data);
      setAudioBlob(null);
      setAudioUrl(null);
      toast.success(M.vcCloneOk);
    } catch (e) {
      toast.error(e?.response?.data?.detail || M.vcCloneKo);
    } finally {
      setUploading(false);
    }
  };

  const deleteClone = async () => {
    if (!window.confirm(M.vcDeleteConfirm)) return;
    try {
      await axios.delete('/api/myia-voice/clone/mine');
      setExisting(null);
      setSynth(null);
      toast.success(M.vcDeleted);
    } catch { toast.error(M.vcCloneKo); }
  };

  const doSynth = async () => {
    if (!text.trim()) return;
    setSynthesizing(true);
    setSynth(null);
    try {
      const r = await axios.post('/api/myia-voice/clone/speak', { text, language: lang });
      const b64 = r.data?.audio_b64;
      if (!b64) throw new Error('no audio');
      const url = `data:audio/mp3;base64,${b64}`;
      setSynth({ url });
    } catch (e) {
      toast.error(e?.response?.data?.detail || M.vcSynthKo);
    } finally {
      setSynthesizing(false);
    }
  };

  return (
    <div className="p-4 rounded-2xl bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/30" data-testid="voice-clone-panel">
      <div className="flex items-start gap-3 mb-3">
        <div className="text-2xl shrink-0">🎙️</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-bold text-cyan-200 text-sm">{M.vcTitle}</h4>
            {premiumOn && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-yellow-500/15 border border-yellow-500/40 text-[9px] font-bold text-yellow-300 uppercase tracking-wide" data-testid="voice-clone-premium-badge">
                <Crown className="w-2.5 h-2.5" /> Premium
              </span>
            )}
          </div>
          <p className="text-[11px] text-cyan-200/70 mt-0.5 leading-snug">
            {M.vcSub}
          </p>
        </div>
      </div>

      {existing ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between p-2.5 rounded-xl bg-black/30 border border-cyan-500/30">
            <div className="text-xs">
              <div className="font-semibold text-cyan-200">{M.vcMineOk} {existing.name ? `— ${existing.name}` : ''}</div>
              <div className="text-cyan-200/60 text-[10px]">ID : {existing.voice_id?.slice(0, 12)}…</div>
            </div>
            <button onClick={deleteClone} className="p-2 rounded-full bg-red-500/20 hover:bg-red-500/30" title="Supprimer" data-testid="voice-clone-delete">
              <Trash2 className="w-3.5 h-3.5 text-red-300" />
            </button>
          </div>
          <div className="flex gap-2">
            <select value={lang} onChange={e => setLang(e.target.value)} className="bg-black/40 border border-cyan-500/40 rounded-full px-3 py-1.5 text-xs" data-testid="voice-clone-lang">
              {LANGS.map(([id, lbl]) => <option key={id} value={id} className="bg-gray-900">{lbl}</option>)}
            </select>
          </div>
          <textarea
            value={text} onChange={e => setText(e.target.value)} rows={3}
            placeholder={M.vcTextPh}
            className="w-full bg-black/30 border border-cyan-500/30 rounded-xl p-2.5 text-xs resize-none focus:outline-none focus:border-cyan-400"
            data-testid="voice-clone-text"
          />
          <button
            onClick={doSynth} disabled={!text.trim() || synthesizing}
            className="w-full py-2.5 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 font-semibold text-xs disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="voice-clone-synth"
          >
            {synthesizing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Languages className="w-4 h-4" />}
            {synthesizing ? M.vcSynthing : M.vcSynth}
          </button>
          {synth && (
            <div className="p-2.5 rounded-xl bg-black/30 border border-cyan-500/30 flex items-center gap-2">
              <button
                onClick={() => {
                  if (!audioRef.current) return;
                  if (playing) { audioRef.current.pause(); setPlaying(false); }
                  else { audioRef.current.play(); setPlaying(true); }
                }}
                className="p-2 rounded-full bg-cyan-500 flex-shrink-0"
                data-testid="voice-clone-play"
              >
                {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 ml-0.5" />}
              </button>
              <audio ref={audioRef} src={synth.url} onEnded={() => setPlaying(false)} className="flex-1 h-8" controls />
              <ReportAIContentButton feature="voice_clone" mediaUrl={synth.url} iconOnly className="p-2 rounded-full bg-white/10 hover:bg-orange-500/30 text-orange-200 flex-shrink-0" testId="ai-report-voice-clone" />
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {!audioUrl && (
            <button
              onClick={recording ? stopRec : startRec}
              className={`w-full py-3 rounded-full font-semibold text-xs flex items-center justify-center gap-2 ${
                recording ? 'bg-red-500 animate-pulse' : 'bg-gradient-to-r from-cyan-500 to-blue-600'
              }`}
              data-testid="voice-clone-record"
            >
              {recording ? <><Square className="w-4 h-4" fill="white" /> {M.vcStop} ({seconds}s)</> : <><Mic className="w-4 h-4" /> {M.vcRecord}</>}
            </button>
          )}
          {audioUrl && (
            <>
              <audio src={audioUrl} controls className="w-full h-8" />
              <div className="flex gap-2">
                <button onClick={() => { setAudioBlob(null); setAudioUrl(null); }} className="flex-1 py-2 rounded-full bg-white/10 text-xs" data-testid="voice-clone-redo">{M.vcRedo}</button>
                <button
                  onClick={upload} disabled={uploading || seconds < 10}
                  className="flex-1 py-2 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 text-xs font-semibold disabled:opacity-50 flex items-center justify-center gap-1"
                  data-testid="voice-clone-upload"
                >
                  {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                  {uploading ? M.vcSending : (seconds < 10 ? `${M.vcMinSec} (${seconds}s)` : M.vcClone)}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
